import { ComponentPropsWithoutRef, createContext, useContext, useMemo } from 'react';
import * as Accordion from '@givebutter/primitives/accordion';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Label, useGivebutterFormContext } from 'components/form';
import { twMerge } from 'tailwind-merge';
import { EventType } from 'types/domain';

interface ISelectTypeContext {
    checked: boolean;
    type: EventType;
}
const SelectTypeContext = createContext<ISelectTypeContext>({
    checked: false,
    type: 'collect',
});

interface ISelectTypeRoot extends ComponentPropsWithoutRef<'div'> {
    type: EventType;
}
const SelectTypeRoot = ({ type, children, ...rest }: ISelectTypeRoot) => {
    const { watch } = useGivebutterFormContext();
    const eventType = watch('type');

    const checked = useMemo(() => eventType === type, [type, eventType]);

    const ctx = useMemo(
        () => ({
            type,
            checked,
        }),
        [type, checked],
    );

    return (
        <SelectTypeContext.Provider value={ctx}>
            <div
                key={`radiogroup-${type}`}
                className={twMerge(
                    '!rounded-2xl bg-white ring-1 ring-gray-200',
                    checked && 'ring-2 ring-blue-500',
                )}
                {...rest}
            >
                <Accordion.Item
                    value={type}
                    className={twMerge('flex flex-col rounded-2xl', checked ? 'gap-2' : 'gap-0')}
                >
                    {children}
                </Accordion.Item>
            </div>
        </SelectTypeContext.Provider>
    );
};

interface ISelectTypeTrigger
    extends Omit<ComponentPropsWithoutRef<typeof RadioGroup.Item>, 'value'> {}
const SelectTypeTrigger = ({ className = '', children, ...rest }: ISelectTypeTrigger) => {
    const { checked, type } = useContext(SelectTypeContext);
    return (
        <Accordion.Trigger asChild>
            <div className="!rounded-2xl !border-b-0 !p-0">
                <RadioGroup.Item
                    id={type}
                    value={type}
                    className={twMerge(
                        'flex h-fit w-full items-center justify-start gap-2 !rounded-2xl border-none bg-white px-5 py-4',
                        checked && '!bg-transparent !pb-0',
                        className,
                    )}
                    {...rest}
                >
                    {children}
                </RadioGroup.Item>
            </div>
        </Accordion.Trigger>
    );
};

interface ISelectTypeTriggerIcon extends ComponentPropsWithoutRef<'div'> {
    icon: IconDefinition;
}
const SelectTypeTriggerIcon = ({ className = '', icon, ...rest }: ISelectTypeTriggerIcon) => {
    const { checked } = useContext(SelectTypeContext);
    return (
        <div className={twMerge('font-medium', className)} {...rest}>
            <div
                className={twMerge(
                    'flex size-8 items-center justify-center rounded-md bg-gray-100 [&>svg]:text-gray-500',
                    checked && '[&>svg]:text-brand-cobalt-primary bg-blue-100',
                    className,
                )}
            >
                <FontAwesomeIcon className="radix-state-checked:text-blue-500" icon={icon} />
            </div>
        </div>
    );
};

interface ISelectTypeTriggerLabel extends Omit<ComponentPropsWithoutRef<typeof Label>, 'name'> {}
const SelectTypeTriggerLabel = ({ className = '', ...rest }: ISelectTypeTriggerLabel) => {
    const { type } = useContext(SelectTypeContext);
    return <Label className={'cursor-pointer select-none text-base'} name={type} {...rest} />;
};

interface ISelectTypeContent extends ComponentPropsWithoutRef<'div'> {}
const SelectTypeContent = ({ className = '', children, ...rest }: ISelectTypeContent) => {
    return (
        <Accordion.Content className="!rounded-b-2xl">
            <div
                className={twMerge('flex flex-col gap-3 px-5 pb-4 text-left', className)}
                {...rest}
            >
                {children}
            </div>
        </Accordion.Content>
    );
};

interface ISelectTypeContentDescription extends ComponentPropsWithoutRef<'p'> {}
const SelectTypeContentDescription = ({
    className = '',
    ...rest
}: ISelectTypeContentDescription) => {
    return <p className={twMerge('text-sm text-gray-500', className)} {...rest} />;
};

interface ISelectTypeContentButton extends Omit<ButtonProps, 'variant'> {}
const SelectTypeContentButton = ({
    className = '',
    onClick,
    ...rest
}: ISelectTypeContentButton) => {
    const { setValue } = useGivebutterFormContext();
    const { type } = useContext(SelectTypeContext);
    return (
        <Button
            aria-hidden
            variant={'solid'}
            color={'primary'}
            className={twMerge(
                'ring-brand-cobalt-primary w-full ring-4 ring-inset focus:bg-opacity-70',
                className,
            )}
            onClick={e => {
                setValue('type', type);
                onClick?.(e);
            }}
            {...rest}
        >
            Get started
        </Button>
    );
};

const Root = SelectTypeRoot;
const Trigger = SelectTypeTrigger;
const TriggerIcon = SelectTypeTriggerIcon;
const TriggerLabel = SelectTypeTriggerLabel;
const Content = SelectTypeContent;
const ContentDescription = SelectTypeContentDescription;
const ContentButton = SelectTypeContentButton;

export {
    SelectTypeRoot,
    SelectTypeTrigger,
    SelectTypeTriggerIcon,
    SelectTypeTriggerLabel,
    SelectTypeContent,
    SelectTypeContentDescription,
    SelectTypeContentButton,
    //
    Root,
    Trigger,
    TriggerIcon,
    TriggerLabel,
    Content,
    ContentDescription,
    ContentButton,
};
